import { Container } from '@/components/Container';
import { AppLink } from '@/components/AppLink';
import { Callout } from '@/components/Callout';
import { Card, CARD_TITLE } from '@/components/Card';
import { DocRow } from '@/components/DocRow';
import { DraftMarker } from '@/components/DraftMarker';
import { PageHeader } from '@/components/PageHeader';
import { useCirculars } from '@/sections/circulars/useCirculars';
import { pickLatest } from '@/lib/circulars';
import { EXAM_DOCS, EXAM_STEPS } from '@/data/content';
import { photos } from '@/data/images';

export function Examinations() {
  const { status, data } = useCirculars();
  const latest = status === 'success' ? pickLatest(data) : undefined;
  // "#" means the client has not sent that circular yet.
  const latestFile = latest && latest.pdfUrl !== '#' ? latest.pdfUrl : undefined;

  return (
    <>
      <PageHeader
        title="Examinations"
        intro="Open Merit Examinations for students sponsored by ITES-affiliated institutes, held at centres across India and abroad."
        image={photos.examPractical}
        objectPosition="center 62%"
      />
      <Container className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,380px),1fr))] items-start gap-8 pt-[clamp(32px,5vw,56px)] pb-[clamp(56px,7vw,96px)]">
        <div className="flex flex-col gap-8">
          {/* The one highlighted card: the circular students need right now. */}
          <Card as="section" aria-labelledby="h-session" className="border-t-4 border-t-teal-700">
            <h2 id="h-session" className={CARD_TITLE}>Current session</h2>
            {latest && (
              <>
                <p className="m-0 font-mono text-[0.75rem] tracking-[0.08em] text-teal-700 uppercase">Latest circular</p>
                <p className="mt-2 mb-0 font-serif text-h4 font-semibold text-navy-900">{latest.title}</p>
                {latestFile ? (
                  <AppLink href={latestFile} className="tap mt-2.5 inline-block font-semibold underline underline-offset-[3px]">
                    Download PDF ↓
                  </AppLink>
                ) : (
                  <p className="mt-2.5 mb-0 font-semibold text-muted">PDF soon</p>
                )}
              </>
            )}
          </Card>

          <Card as="section" aria-labelledby="h-how">
            <h2 id="h-how" className={CARD_TITLE}>How examinations work</h2>
            <ol className="m-0 list-none border-t border-rule p-0">
              {EXAM_STEPS.map((step, i) => (
                <li key={step} className="flex gap-[18px] border-b border-rule py-4 text-[1.125rem] leading-[1.5]">
                  <span className="flex-[0_0_28px] pt-1 font-mono text-[0.8125rem] text-teal-700">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
            <AppLink href="/centres/practical" className="tap mt-4 inline-block font-semibold underline underline-offset-[3px]">
              Find your exam centre →
            </AppLink>
            <div className="mt-4">
              <DraftMarker>DRAFT · ITES to confirm process wording</DraftMarker>
            </div>
          </Card>
        </div>

        <div className="flex flex-col gap-8">
          <Card as="section" aria-labelledby="h-docs">
            <h2 id="h-docs" className={CARD_TITLE}>Examination documents</h2>
            <DraftMarker className="mb-3">DRAFT · latest files on the old site are from 2018–2020</DraftMarker>
            <ul className="m-0 list-none border-t border-rule p-0">
              {EXAM_DOCS.map((d) => (
                <DocRow key={d.title} title={d.title} meta={d.meta} href={d.file} />
              ))}
            </ul>
          </Card>

          <Callout aria-labelledby="h-centres">
            <h3 id="h-centres" className="m-0 font-serif text-h4 font-semibold text-navy-900">
              Exam centres
            </h3>
            <p className="mt-2.5 mb-3.5 text-[1.0625rem] leading-[1.55] text-ink-2">
              Find examination centres by state, district and town.
            </p>
            <AppLink href="/centres/practical" className="tap font-semibold underline">Browse centres →</AppLink>
          </Callout>
        </div>
      </Container>
    </>
  );
}
